import { create } from "zustand";
import { persist } from "zustand/middleware";
import { HissaReportType, ReportStatus } from "../types/common";

type ReportJob = {
  jobId?: string;
  reportType: HissaReportType;
  status: ReportStatus["status"];
  progress?: number;
  data?: any;
  error?: string;
  updatedAt: number;
};

type CompanyReports = Record<HissaReportType, ReportJob>;

type ReportJobStore = {
  reports: Record<string, CompanyReports>;
  upsertReport: (
    companyId: string,
    reportType: HissaReportType,
    payload: Partial<ReportJob>
  ) => void;
  clearReport: (companyId: string, reportType: HissaReportType) => void;
  clearCompany: (companyId: string) => void;
  clearAll: () => void;
};

export const useReportJobStore = create<ReportJobStore>()(
  persist(
    (set) => ({
      reports: {},
      upsertReport: (companyId, reportType, payload) =>
        set((state) => ({
          reports: {
            ...state.reports,
            [companyId]: {
              ...(state.reports[companyId] ?? {}),
              [reportType]: {
                ...(state.reports[companyId]?.[reportType] ?? {}),
                reportType,
                ...payload,
                updatedAt: payload.updatedAt ?? Date.now(),
                error: payload.error ?? undefined,
              },
            },
          },
        })),

      clearReport: (companyId, reportType) =>
        set((state) => {
          const companyReports = state.reports[companyId];
          if (!companyReports) return state;
          const { [reportType]: _, ...rest } = companyReports;
          return {
            reports: {
              ...state.reports,
              [companyId]: rest as CompanyReports,
            },
          };
        }),

      clearCompany: (companyId) =>
        set((state) => {
          const { [companyId]: _, ...rest } = state.reports;
          return { reports: rest };
        }),

      clearAll: () => ({ reports: {} }),
    }),
    {
      name: "job-store",
      partialize: (state) => ({ reports: state.reports }),
    }
  )
);
